import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  LoadCanvasTemplate,
  loadCaptchaEnginge,
  validateCaptcha,
} from "react-simple-captcha";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import logo from "../../../images/logo.png";
import logosvg from "../../../images/logo.svg";
import {
  getPersonalDetails,
  loginApi,
  verifyLoginOTPApi,
} from "../../../environment";
import useApi from "../../../core/customhook/useApi";
import { useAuth } from "../AuthContext";
type PasswordField = "password";

const Login = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const { login } = useAuth();

  const [userCaptcha, setUserCaptcha] = useState("");
  const [authStep, setAuthStep] = useState("login");
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(120);
  const [authData, setAuthData] = useState({
    email: "",
    password: "",
  });

  const userLogin = useApi<any>(loginApi, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const verifyLoginOTP = useApi<any>(verifyLoginOTPApi, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const PersonalDetailAPI = useApi<any>(getPersonalDetails, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const fetchAndStorePersonalDetails = async () => {
    try {
      const response = await PersonalDetailAPI.callApi();

      if (response?.success) {
        localStorage.setItem("userPersonalData", JSON.stringify(response.data));
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (authStep !== "login") return;

    const timeout = setTimeout(() => {
      loadCaptchaEnginge(6);
    }, 100); // small delay so the canvas is mounted before drawing

    return () => clearTimeout(timeout);
  }, [authStep]);

  useEffect(() => {
    if (timer <= 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  const minutes = String(Math.floor(timer / 60)).padStart(2, "0");
  const seconds = String(timer % 60).padStart(2, "0");

  const getInputData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAuthData({ ...authData, [name]: value });
  };

  const validateCaptchaInput = () => {
    if (!userCaptcha) {
      Swal.fire({
        icon: "warning",
        title: "Captcha Required",
        text: "Please enter captcha",
      });

      return false;
    }

    if (!validateCaptcha(userCaptcha)) {
      Swal.fire({
        icon: "error",
        title: "Invalid Captcha",
      });

      setUserCaptcha("");

      loadCaptchaEnginge(6);

      return false;
    }
    return true;
  };

  const handleLogin = async () => {
    try {
      if (!authData.email) {
        Swal.fire({
          icon: "warning",
          title: "Email Required",
        });

        return;
      }

      if (!authData.password) {
        Swal.fire({
          icon: "warning",
          title: "Password Required",
        });

        return;
      }

      if (!validateCaptchaInput()) return;

      const payload = {
        email: authData.email,
        password: authData.password,
      };

      const response = await userLogin.callApi(payload);
      if (response?.success) {
        if (response?.data?.otpSent === false) {
          Swal.fire({
            icon: "success",
            title: "Login Successfully",
          });
          login(response?.data);
          await fetchAndStorePersonalDetails(); // new line
          navigate(routes.caseSummary);
        } else {
          setAuthStep("otp");
          setTimer(120);
          setOtp("");
        }
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: error?.response?.data?.error || "Something went wrong",
      });
    }
  };

  const handleOtpSubmit = async () => {
    // Required validation
    if (!otp.trim()) {
      Swal.fire({
        icon: "warning",
        title: "OTP Required",
        text: "Please enter the OTP.",
      });
      return;
    }

    // 6-digit validation
    if (!/^\d{6}$/.test(otp)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid OTP",
        text: "Please enter a valid 6-digit OTP.",
      });
      return;
    }

    try {
      const payload = {
        email: authData.email,
        otp,
      };

      const response = await verifyLoginOTP.callApi(payload);

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Login Successfully",
        });
        login(response?.data);
        await fetchAndStorePersonalDetails(); // new line
        navigate(routes.caseSummary);
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: error?.response?.data?.error || "Something went wrong",
      });
    }
  };

  const loginLoading = userLogin.loading;
  const otpLoading = verifyLoginOTP.loading;

  const handleResend = async () => {
    try {
      const payload = {
        email: authData.email,
        password: authData.password,
      };

      const response = await userLogin.callApi(payload);

      if (response?.success) {
        setTimer(120);
        setOtp("");
        Swal.fire({
          icon: "success",
          title: "OTP Resent Successfully",
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Failed To Resend OTP",
      });
    }
  };

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
  });

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const maskEmail = (email: string) => {
    if (!email) return "";

    const [name, domain] = email.split("@");

    const maskedName =
      name.length <= 2
        ? name[0] + "*"
        : name[0] + "*".repeat(name.length - 2) + name[name.length - 1];

    const [domainName, extension] = domain.split(".");

    const maskedDomain =
      domainName.length <= 1
        ? domainName
        : domainName[0] + "*".repeat(domainName.length - 1);

    return `${maskedName}@${maskedDomain}.${extension}`;
  };
  return (
    <div className="container-fluid p-0">
      <div className="row g-0 vh-100">
        {/* RIGHT SIDE - CONTENT + SCROLLING IMAGES */}
        <div className="col-lg-7 d-none d-lg-block">
          <div
            className="vh-100 position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            }}
          >
            {/* CONTENT */}
            <div
              className="position-absolute top-50 start-50 translate-middle text-center text-white px-2"
              style={{ zIndex: 2 }}
            >
              <img
                src={logosvg}
                alt="logo"
                className="img-fluid mb-4"
                style={{ maxWidth: "220px" }}
              />

              <h1
                className="fw-bold mb-4"
                style={{ fontSize: "28px", color: "white" }}
              >
                Welcome to the Aupertz Portal
              </h1>

              <p
                className="text-light"
                style={{ fontSize: "18px", lineHeight: "32px" }}
              >
                Sign in to access your background verification application,
                upload required documents, track verification progress, and
                complete your onboarding seamlessly.
              </p>
            </div>

            {/* SCROLLING IMAGES */}
            <div className="scroll-images">
              <img
                src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d"
                alt=""
              />
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978"
                alt=""
              />
              <img
                src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3"
                alt=""
              />
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f"
                alt=""
              />
            </div>
          </div>
        </div>

        <div className="col-lg-5 col-md-12">
          <div className="d-flex align-items-center justify-content-center vh-100 bg-white px-4">
            {/* ─── LOGIN PAGE ─── shown only when authStep === "login" */}
            {authStep === "login" && (
              <div
                style={{
                  width: "100%",
                  maxWidth: "480px",
                  margin: "0 auto",
                }}
              >
                <div className="login-card">
                  <div className="text-center mb-1">
                    <img
                      src={logo}
                      alt="logo"
                      className="img-fluid"
                      style={{ maxWidth: "170px" }}
                    />
                  </div>

                  <div className="mb-4 text-center">
                    <h2 className="login-title mb-1">Welcome back</h2>
                    <p className="login-subtitle mb-0">Aupertz Portal Login</p>
                    <span className="login-tagline">
                      Instant Background Screening
                    </span>
                  </div>

                  <form>
                    <div className="login_form_main">
                      <div className="d-flex flex-column">
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Username / Email
                            <span className="text-danger ms-1">*</span>
                          </label>
                          <div className="input-group input-group-custom">
                            <input
                              type="text"
                              value={authData.email}
                              className="form-control"
                              name="email"
                              placeholder="Enter your username or email"
                              onChange={getInputData}
                            />
                            <span className="input-group-text border-start-0">
                              <i className="ti ti-mail" />
                            </span>
                          </div>
                        </div>

                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Password <span className="text-danger ms-1">*</span>
                          </label>
                          <div className="pass-group">
                            <input
                              type={
                                passwordVisibility.password
                                  ? "text"
                                  : "password"
                              }
                              className="form-control"
                              name="password"
                              placeholder="Enter your password"
                              onChange={getInputData}
                            />
                            <span
                              className={`ti toggle-passwords ${
                                passwordVisibility.password
                                  ? "ti-eye"
                                  : "ti-eye-off"
                              }`}
                              onClick={() =>
                                togglePasswordVisibility("password")
                              }
                            ></span>
                          </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-end mb-3">
                          <Link
                            to={all_routes.forgotPassword}
                            className="text-danger fw-bold"
                          >
                            Forgot Password?
                          </Link>
                        </div>

                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Security Check
                            <span className="text-danger ms-1">*</span>
                          </label>

                          {authStep === "login" && (
                            <div className="captcha-row mb-2">
                              <div className="captcha-box">
                                <LoadCanvasTemplate />
                              </div>
                              <button
                                type="button"
                                className="captcha-reload-btn"
                                title="Get a new code"
                                onClick={() => {
                                  loadCaptchaEnginge(6);
                                  setUserCaptcha("");
                                }}
                              >
                                <i className="ti ti-refresh" />
                              </button>
                            </div>
                          )}

                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter the code shown above"
                            value={userCaptcha}
                            onChange={(e) => setUserCaptcha(e.target.value)}
                          />
                        </div>

                        <div className="mb-2">
                          <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={handleLogin}
                            disabled={loginLoading}
                          >
                            {loginLoading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                />
                                Logging in...
                              </>
                            ) : (
                              "Log In"
                            )}
                          </button>
                        </div>

                        <div className="login_footer">
                          {/* <p className="footer-policies">
                            This site is compliant to{" "}
                            <Link to={"#"}>India Privacy Policy</Link>
                            <span className="dot">&middot;</span>
                            <Link to={"#"}>EU</Link>
                            <span className="dot">&middot;</span>
                            <Link to={"#"}>GDPR Privacy Policy</Link>
                            <span className="dot">&middot;</span>
                            <Link to={"#"}>Cookie Policy</Link>
                          </p> */}
                          <div className="footer-info-container">
                            <div className="footer-info-item">
                              <p className="footer-line mb-0">
                                <span className="footer-highlight">
                                  For client login, please visit{" "}
                                </span>{" "}
                                <Link className="footer-link-blue" to="#">
                                  candidatesupport@aupertz.co.in
                                </Link>
                              </p>
                            </div>

                            {/* <div className="footer-divider"></div> */}

                            {/* <div className="footer-info-item">
                              <p className="footer-line mb-0">
                                <span className="footer-highlight">
                                  Candidate Resources:
                                </span>{" "}
                                <Link className="footer-link-blue" to="#">
                                  Click here
                                </Link>
                              </p>
                            </div> */}
                          </div>

                          <p className="footer-line">
                            <span className="footer-highlight">
                              In case you are facing any issue please write to
                              us at{" "}
                            </span>
                            <Link
                              className="footer-link-blue"
                              to={"mailto:candidatesupport@aupertz.co.in"}
                            >
                              candidatesupport@aupertz.co.in
                            </Link>
                          </p>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* ─── OTP PAGE ─── shown only when authStep === "otp" */}
            {authStep === "otp" && (
              <div
                style={{
                  width: "100%",
                  maxWidth: "480px",
                  margin: "0 auto",
                }}
              >
                <div className="login-card">
                  <div className="text-center mb-3">
                    <img
                      src={logo}
                      alt="logo"
                      className="img-fluid"
                      style={{ maxWidth: "170px" }}
                    />
                  </div>

                  <div className="mb-4 text-center">
                    <h2 className="login-title mb-2">OTP Authentication</h2>
                    <p className="login-subtitle-muted mb-0">
                      You will receive a 6 digit OTP on your registered email
                    </p>
                    <p className="otp-email-highlight">
                      {maskEmail(authData.email)}
                    </p>
                  </div>

                  <form>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Enter OTP
                      </label>
                      <div className="otp-input-row">
                        <input
                          type="number"
                          className="custom-otp-input"
                          placeholder="Enter 6 digit code"
                          value={otp}
                          maxLength={6}
                          onChange={(e) => setOtp(e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          disabled={timer > 0 || userLogin.loading}
                          onClick={handleResend}
                        >
                          {userLogin.loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Resending...
                            </>
                          ) : (
                            "Resend OTP"
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="mb-3">
                      <button
                        type="button"
                        className="btn btn-primary w-100 login-submit-btn"
                        disabled={otpLoading}
                        onClick={handleOtpSubmit}
                      >
                        {otpLoading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            />
                            Verifying OTP...
                          </>
                        ) : (
                          "Verify & Continue"
                        )}
                      </button>
                    </div>

                    {/* TIMER + RESEND */}
                    <div className="custom-otp-bottom-row">
                      <p className="custom-otp-timer">
                        <i className="ti ti-clock-hour-4 me-1"></i>
                        OTP expires in{" "}
                        <span className="otp-countdown">
                          {minutes}:{seconds}
                        </span>
                      </p>

                      <button
                        type="button"
                        className="custom-otp-resend-btn"
                        disabled={timer > 0 || userLogin.loading}
                        onClick={handleResend}
                      >
                        {userLogin.loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Resending...
                          </>
                        ) : (
                          "Resend"
                        )}
                      </button>
                    </div>

                    {/* FOOTER */}
                    <p className="custom-otp-footer">
                      In case you are facing any issues please write to us at{" "}
                      <span className="text-danger">
                        candidatesupport@aupertz.co.in
                      </span>
                    </p>

                    {/* Back to login link */}
                    <div className="text-center mt-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => setAuthStep("login")}
                      >
                        ← Back to Login
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
